import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Breadcrumbs from '../components/Breadcrumbs';
import { FaPhone, FaMapMarkerAlt, FaHome, FaAddressBook, FaTv, FaBroadcastTower, FaExternalLinkAlt } from 'react-icons/fa';
import { getFileUrl } from '../utils/fileUtils';
import api from '../services/api';
import './DirectorioDetalle.css';

function youtubeEmbedUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  const match = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

const DirectorioDetalle = () => {
  const { clave } = useParams();
  const [imagenPrincipal, setImagenPrincipal] = useState(0);

  const claveNorm = (clave || '').replace(/-/g, '_');

  const { data: seccion, isLoading, error } = useQuery({
    queryKey: ['seccion-directorio', claveNorm],
    queryFn: async () => {
      const res = await api.get(`/secciones-directorio/${claveNorm}`);
      return res.data;
    },
    enabled: !!claveNorm,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="directorio-detalle-page">
        <Breadcrumbs />
        <div className="container"><p className="loading">Cargando...</p></div>
      </div>
    );
  }
  if (error || !seccion) {
    return (
      <div className="directorio-detalle-page">
        <Breadcrumbs />
        <div className="container">
          <section className="section">
            <h1 className="page-title">Sección no encontrada</h1>
            <p>No existe una sección del directorio con esta dirección.</p>
            <Link to="/servicios" className="directorio-detalle-back-link">
              <FaHome /> Volver a Directorio
            </Link>
          </section>
        </div>
      </div>
    );
  }

  const unaSolaFoto = seccion.una_sola_foto === 1 || (seccion.imagenes && seccion.imagenes.length <= 1);
  const imagenes = (seccion.imagenes || []).map((i) => (typeof i === 'string' ? i : i?.imagen_url || i));
  const placeholder = '/images/placeholder-casa.svg';
  const urlExterna = (seccion.url_externa || '').trim();
  const embedUrl = youtubeEmbedUrl(seccion.video_url);

  // Determinar icono según clave (TV vs Radio)
  const isTv = /tv|ipiales/.test(claveNorm);
  const isRadio = /radio|rumba|emisora/.test(claveNorm);
  const ExtIcon = isTv ? FaTv : isRadio ? FaBroadcastTower : FaExternalLinkAlt;

  return (
    <div className="directorio-detalle-page">
      <Breadcrumbs />
      <section className="section directorio-detalle-hero">
        <div className="container">
          <h1 className="page-title"><FaAddressBook aria-hidden /> {seccion.titulo}</h1>
        </div>
      </section>

      <section className="section directorio-detalle-content">
        <div className="container">
          <div className="directorio-detalle-grid">
            <div className="directorio-detalle-imagenes">
              {imagenes.length > 0 ? (
                unaSolaFoto ? (
                  <div className="directorio-detalle-imagen-unica">
                    <img src={getFileUrl(imagenes[0]) || placeholder} alt={seccion.titulo} onError={(e) => { e.target.src = placeholder; }} />
                  </div>
                ) : (
                  <>
                    <div className="directorio-detalle-imagen-principal">
                      <img src={getFileUrl(imagenes[imagenPrincipal] || imagenes[0]) || placeholder} alt={`${seccion.titulo} - ${imagenPrincipal + 1}`} onError={(e) => { e.target.src = placeholder; }} />
                    </div>
                    {imagenes.length > 1 && (
                      <div className="directorio-detalle-galeria-mini">
                        {imagenes.map((url, idx) => (
                          <button key={idx} type="button" className={`miniatura ${imagenPrincipal === idx ? 'active' : ''}`} onClick={() => setImagenPrincipal(idx)} aria-label={`Ver imagen ${idx + 1}`}>
                            <img src={getFileUrl(url) || placeholder} alt="" onError={(e) => { e.target.src = placeholder; }} />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )
              ) : seccion.imagen_url ? (
                <div className="directorio-detalle-imagen-unica">
                  <img src={getFileUrl(seccion.imagen_url) || placeholder} alt={seccion.titulo} onError={(e) => { e.target.src = placeholder; }} />
                </div>
              ) : null}
            </div>
            <div className="directorio-detalle-info">
              {seccion.texto && (
                <div className="directorio-detalle-texto" dangerouslySetInnerHTML={{ __html: seccion.texto }} />
              )}
              {embedUrl && (
                <div className="directorio-detalle-video">
                  <iframe title={`Video ${seccion.titulo}`} src={embedUrl} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              )}
              {urlExterna && (
                <div className="directorio-detalle-enlace-externo">
                  <a
                    href={urlExterna}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary directorio-detalle-btn-externo"
                  >
                    <ExtIcon aria-hidden /> {isTv ? 'Ver TV en vivo' : isRadio ? 'Escuchar en la web' : 'Abrir en nueva ventana'}
                  </a>
                </div>
              )}
              {(seccion.telefono || seccion.direccion) && (
                <div className="directorio-detalle-contacto">
                  {seccion.telefono && (
                    <p className="directorio-detalle-contacto-item">
                      <FaPhone aria-hidden />
                      <a href={`tel:${String(seccion.telefono).replace(/\D/g, '')}`}>{seccion.telefono}</a>
                    </p>
                  )}
                  {seccion.direccion && (
                    <p className="directorio-detalle-contacto-item">
                      <FaMapMarkerAlt aria-hidden />
                      <span>{seccion.direccion}</span>
                    </p>
                  )}
                </div>
              )}
              {!seccion.texto && !seccion.telefono && !seccion.direccion && !urlExterna && !embedUrl && (
                <p className="directorio-detalle-sin-datos">Contenido disponible próximamente.</p>
              )}
              <Link to="/servicios" className="directorio-detalle-back-link">
                ← Volver a Directorio
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DirectorioDetalle;
